// Pure helpers for ProjectDetailsRoute. Ports the legacy
// frontend/app.jsx `projectToMeta` + `metaToProjectPayload` +
// `PROJECT_DETAILS_GROUPS` verbatim so the v2 saves the same shape
// the backend `POST /projects/put` already validates against.
import type { Project, StatusNote } from "../api";

// Flat editable form state. StatusNote sub-models collapse to their
// `summary` string so the form is a flat key→string map; saving
// re-inflates them with timestamp + author. Numeric / boolean
// fields are kept as strings here to keep the form input simple;
// coercion happens at save time.
export type ProjectMeta = Record<ProjectMetaField, string>;

export type ProjectMetaField =
  // Identity
  | "customer_code"
  | "customer_business_unit"
  | "airport_code"
  // Site fundamentals
  | "address"
  | "landlord"
  | "operation_type"
  | "location_type"
  | "ownership_type"
  | "lease_expiry"
  | "remaining_lease_years"
  // PV system
  | "target_system_size_kwp"
  | "existing_pv_summary"
  | "has_battery"
  | "primary_epc"
  | "backup_epc"
  // Grid
  | "mpan"
  | "dno"
  | "idno"
  // Notes
  | "comments"
  // Status notes (StatusNote.summary)
  | "shovel_readiness"
  | "on_site_survey"
  | "roof_condition"
  | "structural"
  | "landlord_consent"
  | "epc_cost"
  | "roof_warranty"
  | "mfl_exclusion";

export const STATUS_NOTE_META_FIELDS: ReadonlySet<ProjectMetaField> = new Set([
  "shovel_readiness",
  "on_site_survey",
  "roof_condition",
  "structural",
  "landlord_consent",
  "epc_cost",
  "roof_warranty",
  "mfl_exclusion",
]);

export const NUMERIC_META_FIELDS: ReadonlySet<ProjectMetaField> = new Set([
  "remaining_lease_years",
  "target_system_size_kwp",
]);

// Closed-enum values the backend Pydantic `Literal`s accept on the
// Project model. Free text doesn't validate, so the FE must render
// these as <select> rather than <input>. Mirrors the legacy
// `normaliseOperationType` / `normaliseOwnershipType` fixups in
// `frontend/app.jsx:1094-1110`, but as a constraint at edit time
// rather than a coercion at save time - the operator sees the
// allowed values directly.
export const OPERATION_TYPE_OPTIONS = [
  "AR Sortable",
  "Delivery Station",
  "Build to Suit",
  "Existing",
  "Other",
] as const;

export const OWNERSHIP_TYPE_OPTIONS = [
  "Leased",
  "Owner Occupier",
  "Other",
] as const;

export const CUSTOMER_CODE_OPTIONS = ["AMZ", "OTHER"] as const;

export const HAS_BATTERY_OPTIONS = ["yes", "no"] as const;

interface GroupRow {
  field: ProjectMetaField;
  label: string;
  placeholder?: string;
  multi?: boolean;
  // When present, the field renders as a <select> with these
  // options + an empty "(none)" choice. Free-text typing is not
  // allowed; this is the load-bearing fix for the round-1 BLOCKER
  // ('leased' would 422 the entire save).
  options?: readonly string[];
}

export interface Group {
  label: string;
  rows: GroupRow[];
}

// Sectioned field layout. Mirrors the legacy `PROJECT_DETAILS_GROUPS`
// 1:1; the operator's muscle memory should transfer.
export const PROJECT_DETAILS_GROUPS: Group[] = [
  {
    label: "Identity",
    rows: [
      {
        field: "customer_code",
        label: "Customer code",
        options: CUSTOMER_CODE_OPTIONS,
      },
      { field: "customer_business_unit", label: "Business unit", placeholder: "AMZL, CF, ..." },
      { field: "airport_code", label: "Airport code", placeholder: "BRS2, LPL2, ..." },
    ],
  },
  {
    label: "Site fundamentals",
    rows: [
      { field: "address", label: "Address", placeholder: "Free-text site address" },
      { field: "landlord", label: "Landlord", placeholder: "Legal & General, Logicor, ..." },
      {
        field: "operation_type",
        label: "Operation type",
        options: OPERATION_TYPE_OPTIONS,
      },
      { field: "location_type", label: "Location type", placeholder: "Urban warehouse, peri-urban, ..." },
      {
        field: "ownership_type",
        label: "Ownership",
        options: OWNERSHIP_TYPE_OPTIONS,
      },
      { field: "lease_expiry", label: "Lease expiry", placeholder: "2042-09-30" },
      { field: "remaining_lease_years", label: "Remaining lease (years)", placeholder: "12.5" },
    ],
  },
  {
    label: "PV system",
    rows: [
      { field: "target_system_size_kwp", label: "Target size (kWp)", placeholder: "850.0" },
      {
        field: "existing_pv_summary",
        label: "Existing PV",
        multi: true,
        placeholder: "Operational 446 kWp, 1088x JA Solar JAM72S10-410Wp, 4x SolarEdge inverters",
      },
      {
        field: "has_battery",
        label: "Has battery",
        options: HAS_BATTERY_OPTIONS,
      },
      { field: "primary_epc", label: "Primary EPC", placeholder: "Sol PV, BSP, ..." },
      { field: "backup_epc", label: "Backup EPC", placeholder: "" },
    ],
  },
  {
    label: "Grid",
    rows: [
      { field: "mpan", label: "MPAN(s)", placeholder: "Comma-separated 13- or 21-digit" },
      { field: "dno", label: "DNO", placeholder: "NGED, UKPN, ..." },
      { field: "idno", label: "IDNO", placeholder: "ESP, Energy Assets, ..." },
    ],
  },
  {
    label: "Notes",
    rows: [
      { field: "comments", label: "Comments", multi: true, placeholder: "Anything else operationally relevant" },
    ],
  },
  {
    label: "Status",
    rows: [
      { field: "shovel_readiness", label: "Shovel readiness", multi: true },
      { field: "on_site_survey", label: "On-site survey", multi: true },
      { field: "roof_condition", label: "Roof condition", multi: true },
      { field: "structural", label: "Structural", multi: true },
      { field: "landlord_consent", label: "Landlord consent", multi: true },
      { field: "epc_cost", label: "EPC cost", multi: true },
      { field: "roof_warranty", label: "Roof warranty", multi: true },
      { field: "mfl_exclusion", label: "MFL / exclusion", multi: true },
    ],
  },
];

// Flat list of every meta field - used to initialise the empty
// state + iterate without re-flattening the groups.
export const ALL_META_FIELDS: readonly ProjectMetaField[] =
  PROJECT_DETAILS_GROUPS.flatMap((g) => g.rows.map((r) => r.field));

export const emptyProjectMeta = (): ProjectMeta =>
  Object.fromEntries(ALL_META_FIELDS.map((f) => [f, ""])) as ProjectMeta;

// Hydrate the editable meta from a Project row pulled off the
// backend. StatusNote sub-models collapse to their summary string;
// boolean has_battery becomes "yes" / "no" so the input shows what
// the operator typed; numbers cast to their string repr.
export const projectToMeta = (project: Project | null | undefined): ProjectMeta => {
  const meta = emptyProjectMeta();
  if (!project) return meta;
  for (const field of ALL_META_FIELDS) {
    const v = (project as unknown as Record<string, unknown>)[field];
    if (v == null) continue;
    if (typeof v === "object" && v !== null && "summary" in v) {
      const note = v as StatusNote;
      meta[field] = note.summary ?? "";
    } else if (typeof v === "boolean") {
      meta[field] = v ? "yes" : "no";
    } else {
      meta[field] = String(v);
    }
  }
  return meta;
};

// Translate the flat editable meta state back into the Project
// payload shape `POST /projects/put` expects. Empty fields are
// OMITTED (not nulled) so they don't overwrite values the bulk
// import did populate. StatusNote re-stamping rules match the
// legacy: re-stamp updated_at + updated_by only when the summary
// text actually changed - otherwise the prior tracker timestamp +
// author survive (the 2026-05-12 stamping regression in legacy).
export const metaToProjectPayload = (args: {
  meta: ProjectMeta;
  projectId: string;
  originalMeta: ProjectMeta;
  originalProject: Project | null | undefined;
  updatedBy: string;
}): Record<string, unknown> => {
  const { meta, projectId, originalMeta, originalProject, updatedBy } = args;
  const out: Record<string, unknown> = { project_id: projectId };
  for (const field of ALL_META_FIELDS) {
    const raw = meta[field];
    const trimmed = (raw ?? "").trim();
    const wasPopulated = (originalMeta[field] ?? "").trim() !== "";
    if (trimmed === "") {
      // CLEAR semantics: only emit an explicit clear payload when
      // the field was non-empty originally AND the operator
      // emptied it. New / never-populated fields stay omitted so
      // we don't churn the row with redundant nulls (the bulk-
      // import provenance lives in those default-null slots).
      if (wasPopulated) {
        if (STATUS_NOTE_META_FIELDS.has(field)) {
          // The backend's `StatusNote` field is non-optional
          // (`Field(default_factory=StatusNote)` in projects.py),
          // so a bare null 422s. Send a fully-nulled sub-object
          // instead - this is a valid StatusNote where every
          // inner field is the schema's existing nullable default.
          out[field] = {
            summary: null,
            updated_at: null,
            updated_by: null,
          };
        } else {
          // Scalar / Optional fields accept null and that's what
          // means "cleared" on the row.
          out[field] = null;
        }
      }
      continue;
    }

    if (STATUS_NOTE_META_FIELDS.has(field)) {
      const changed = raw !== (originalMeta[field] ?? "");
      const priorNote = originalProject
        ? ((originalProject as unknown as Record<string, StatusNote | null>)[field] ??
          null)
        : null;
      out[field] = changed
        ? {
            summary: raw,
            updated_at: new Date().toISOString(),
            updated_by: updatedBy,
          }
        : {
            summary: raw,
            updated_at: priorNote?.updated_at ?? null,
            updated_by: priorNote?.updated_by ?? null,
          };
      continue;
    }

    if (NUMERIC_META_FIELDS.has(field)) {
      const num = Number(raw);
      if (Number.isFinite(num)) out[field] = num;
      continue;
    }

    if (field === "has_battery") {
      // The <select> emits exactly "yes" or "no" (the only two
      // HAS_BATTERY_OPTIONS values), so coercion is a 1:1 map.
      // No silent fallback - any unexpected value comes from
      // stale data and should surface as a backend validation
      // error rather than be silently rewritten.
      out[field] = raw === "yes";
      continue;
    }

    if (field === "customer_code") {
      // The <select> constrains to CUSTOMER_CODE_OPTIONS = ["AMZ",
      // "OTHER"]. Pass through unchanged; let the backend
      // Pydantic Literal validate any out-of-band value loudly,
      // per the CLAUDE.md "fail loudly over silently" rule.
      out[field] = raw.trim();
      continue;
    }

    out[field] = raw.trim();
  }
  // Audit trail - the backend records this on the row so the
  // operational dashboard can show "last edited via the showcase UI".
  out.last_synced_source = "showcase-ui-edit";
  return out;
};

// Count the populated fields - drives the "N of M fields populated"
// counter at the top of the route.
export const countPopulatedFields = (meta: ProjectMeta): number =>
  ALL_META_FIELDS.filter((f) => (meta[f] ?? "").trim().length > 0).length;
